/**
 * Complemento obrigatório de dados do acompanhante do dia.
 *
 * Aparece quando o paciente escolhido como acompanhante não tem os mínimos
 * (nome completo, CPF, telefone — ver `accompanistRequirements`). Grava os
 * campos que faltam na FICHA do paciente (updatePatient) e devolve o paciente
 * já completo pra quem chamou fazer o vínculo — assim o dado fica no cadastro,
 * não só na consulta do dia.
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { PatientService } from '@/services/firebase/patients';
import { formatCPF, formatPhone } from '@/utils/formatters';
import { isValidCPF } from '@/utils/validators';
import {
  formatMissingFields,
  isFullName,
  isUsablePhone,
  type AccompanistField,
} from '@/utils/accompanistRequirements';
import type { Patient } from '@/types/models';

interface Props {
  clinicId: string | null | undefined;
  patient: Patient;
  /** Campos obrigatórios ausentes (de `missingAccompanistData`). */
  missing: AccompanistField[];
  /** Paciente já atualizado, pronto pra ser vinculado como acompanhante. */
  onCompleted: (patient: Patient) => void;
  onCancel: () => void;
}

export function AccompanistDataForm({
  clinicId,
  patient,
  missing,
  onCompleted,
  onCancel,
}: Props) {
  const [name, setName] = useState(patient.name || '');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState(formatPhone(patient.phone || ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsName = missing.includes('name');
  const needsCpf = missing.includes('cpf');
  const needsPhone = missing.includes('phone');

  const handleSave = async () => {
    if (saving) return;
    if (!clinicId) {
      setError('Clínica não identificada. Faça login novamente.');
      return;
    }

    const updates: Partial<Patient> = {};

    if (needsName) {
      if (!isFullName(name)) {
        setError('Informe o nome completo do acompanhante (nome e sobrenome).');
        return;
      }
      updates.name = name.trim();
    }

    if (needsCpf) {
      const digits = cpf.replace(/\D/g, '');
      if (digits.length !== 11) {
        setError(`CPF incompleto: o CPF tem 11 dígitos (foram digitados ${digits.length}).`);
        return;
      }
      if (!isValidCPF(digits)) {
        setError('CPF inválido — confira os números digitados.');
        return;
      }
      updates.cpf = digits;
    }

    if (needsPhone) {
      if (!isUsablePhone(phone)) {
        setError('Telefone inválido — informe DDD + número (10 ou 11 dígitos).');
        return;
      }
      // Grava só dígitos, como todo o resto do sistema. Antes gravava
      // `formatPhone(phone)` — o único ponto do app que persistia telefone
      // COM máscara, o que fazia a busca por telefone e a detecção de
      // duplicata (ambas comparam dígitos) nunca acharem esse paciente.
      // A máscara continua existindo, mas só no input (ver o render).
      updates.phone = phone.replace(/\D/g, '');
    }

    setSaving(true);
    setError(null);
    try {
      await PatientService.updatePatient(clinicId, patient.id, updates);
      // Devolve o paciente em memória já completo. O CPF vai em CLARO (11
      // dígitos) — os consumidores tratam esse caso sem tentar descriptografar
      // (ver useAccompanistChange). `patientNumber` acompanha o CPF, que é o
      // campo lido pela checagem de completude.
      onCompleted({
        ...patient,
        ...updates,
        ...(updates.cpf ? { patientNumber: updates.cpf } : {}),
      });
    } catch (err) {
      console.error('[acompanhante] completar dados falhou:', err);
      setError('Não foi possível salvar os dados. Tente de novo.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-lg bg-surface text-on-surface border border-outline/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50';

  return (
    <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle size={15} className="text-warning shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-on-surface">
            Faltam dados obrigatórios do acompanhante
          </p>
          <p className="text-xs text-on-surface-variant">
            <strong className="text-on-surface">{patient.name}</strong> está sem{' '}
            {formatMissingFields(missing)}. O acompanhante do dia exige nome completo,
            CPF e telefone — preencha para vincular (salva na ficha dele).
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {needsName && (
          <div>
            <label className="block text-[11px] uppercase font-bold tracking-wide text-on-surface-variant mb-1">
              Nome completo *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="Nome e sobrenome"
              autoComplete="off"
              className={inputClass}
            />
          </div>
        )}
        {needsCpf && (
          <div>
            <label className="block text-[11px] uppercase font-bold tracking-wide text-on-surface-variant mb-1">
              CPF *
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={cpf}
              onChange={(e) => {
                setCpf(formatCPF(e.target.value));
                setError(null);
              }}
              placeholder="000.000.000-00"
              autoComplete="off"
              className={inputClass}
            />
          </div>
        )}
        {needsPhone && (
          <div>
            <label className="block text-[11px] uppercase font-bold tracking-wide text-on-surface-variant mb-1">
              Telefone / WhatsApp *
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={phone}
              onChange={(e) => {
                setPhone(formatPhone(e.target.value));
                setError(null);
              }}
              placeholder="(11) 99999-9999"
              autoComplete="off"
              className={inputClass}
            />
          </div>
        )}
      </div>

      {error && <p className="text-xs text-error">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-on-primary text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving && <Loader2 size={13} className="animate-spin" />}
          {saving ? 'Salvando…' : 'Salvar e vincular'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-2 rounded-lg border border-outline-variant/40 text-on-surface-variant text-xs font-medium hover:bg-surface-container transition-colors disabled:opacity-60"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
